import mergeWith from 'lodash/mergeWith';
import qs from 'qs';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DeepPartial } from '../../../types/generic';
import { ParsedManageItemsParams, parseManageItemsParamsFromSearch } from './utils';

/**
 * Custom merge function that replaces arrays instead of merging them.
 * Only deep merges plain objects.
 */
const mergeCustomizer = (objValue: any, srcValue: any) => {
	// If source value is an array, replace the target array entirely
	if (Array.isArray(srcValue)) {
		return srcValue;
	}
	// Otherwise, use default merge behavior (deep merge objects)
	return undefined;
};

/**
 * Hook for managing URL query state for the manage-items page.
 * Works both in components and can be used for generating params in route loaders.
 *
 * @returns A tuple of [params, setParams] where:
 *   - params: Parsed and validated query parameters from the URL
 *   - setParams: Function to update the query parameters
 *
 * @example
 * ```tsx
 * const [queries, setQueries] = useManageItemsQueryState();
 *
 * // Read current page
 * console.log(queries.pageNumber); // 1
 *
 * // Update page number
 * setQueries({ pageNumber: 2 });
 * ```
 */
export const useManageItemsQueryState = () => {
	const location = useLocation();
	const navigate = useNavigate();

	const search = location.search.slice(1);
	const params = parseManageItemsParamsFromSearch(search);

	const handleSetParams = useCallback(
		(updates: DeepPartial<ParsedManageItemsParams>) => {
			// Merge the updates with current params
			// Arrays are replaced entirely, objects are deep merged
			const currentParams = parseManageItemsParamsFromSearch(search);
			const mergedParams = mergeWith({}, currentParams, updates, mergeCustomizer);

			// Convert to query string using qs
			const newSearch = qs.stringify(mergedParams, {
				addQueryPrefix: true,
				skipNulls: true, // Don't include null values
			});

			// Navigate to the new URL
			navigate({
				pathname: location.pathname,
				search: newSearch,
			});
		},
		[search, location.pathname, navigate]
	);

	return [params, handleSetParams] as const;
};

/**
 * Standalone function to parse manage-items params from a search string.
 * Useful for route loaders where hooks cannot be used.
 *
 * @param search - The URL search string (with or without leading '?')
 * @returns Parsed and validated parameters
 *
 * @example
 * ```ts
 * // In a route loader
 * export const loader = ({ request }: LoaderFunctionArgs) => {
 *   const url = new URL(request.url);
 *   const params = getManageItemsParamsFromUrl(url.search);
 *   return fetchData(params);
 * };
 * ```
 */
export const getManageItemsParamsFromUrl = (search: string): ParsedManageItemsParams => {
	// Remove leading '?' if present
	const cleanSearch = search.startsWith('?') ? search.slice(1) : search;
	return parseManageItemsParamsFromSearch(cleanSearch);
};
